import { useEffect, useId, useRef, useState } from "react";
import { useI18n } from "../lib/i18n";

function InformationValue({ value, label, plain = false }: { value: string; label: string; plain?: boolean }) {
  const { t } = useI18n();
  const valueId = useId();
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const paragraph = paragraphRef.current;
    if (!paragraph) return;
    let active = true;
    // Measure the two-line budget even while expanded. Fonts can change wrapping
    // without resizing a clamped paragraph; ignore late font callbacks after unmount.
    const measure = () => {
      if (!active) return;
      const hasOverflow = paragraph.scrollHeight > Number.parseFloat(getComputedStyle(paragraph).lineHeight) * 2 + 1;
      setOverflows(hasOverflow);
      if (!hasOverflow) setExpanded(false);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(paragraph);
    void document.fonts.ready.then(measure);
    document.fonts.addEventListener("loadingdone", measure);
    return () => {
      active = false;
      observer.disconnect();
      document.fonts.removeEventListener("loadingdone", measure);
    };
  }, [value]);

  const action = t(expanded ? "common.showLess" : "common.showMore");
  return <>
    <p id={valueId} ref={paragraphRef} className={`break-words [overflow-wrap:anywhere] ${expanded ? "" : "line-clamp-2"} ${plain ? "text-left" : "text-right"}`}>{value}</p>
    {overflows && <button type="button" className="mt-1 text-xs font-semibold text-navy underline underline-offset-2" aria-expanded={expanded} aria-controls={valueId} aria-label={`${action} ${label}`} onClick={() => setExpanded((shown) => !shown)}>{action}</button>}
  </>;
}

export function ProductInformation({ text }: { text?: string | null }) {
  const { t } = useI18n();
  // [items.product-information](../../../docs/product/items.md#items-product-information): only an empty value after the first separator is an unfilled
  // prompt. Values may contain colons; retain custom prose and URL lines.
  const lines = (text || "").split(/\r?\n/).map((line) => line.trim()).filter((line) => {
    if (!line) return false;
    const separator = line.indexOf(":");
    return separator <= 0 || Boolean(line.slice(separator + 1).trim());
  });
  if (!lines.length) return null;

  return <section className="card p-5" aria-label={t("posting.productInformation", "Product information")}>
    <h2 className="text-base font-bold leading-6 text-navy max-sm:text-[17px] max-sm:font-extrabold">{t("posting.productInformation", "Product information")}</h2>
    <div className="mt-3 space-y-2 text-sm leading-6 text-navy-700">{lines.map((line, index) => {
      const separator = line.indexOf(":");
      const value = line.slice(separator + 1).trim();
      // [items.product-information](../../../docs/product/items.md#items-product-information): disclosure clips presentation only; changing a source row resets
      // its expansion, and the complete original value stays available on both layouts.
      return separator > 0 && separator <= 80 && value && !value.startsWith("//")
        ? <dl key={`${index}:${line}`}><div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-x-3"><dt className="min-w-0 break-words [overflow-wrap:anywhere] text-mut">{line.slice(0, separator)}:</dt><dd className="min-w-0 text-right font-semibold text-navy"><InformationValue value={value} label={line.slice(0, separator)} /></dd></div></dl>
        : <div key={`${index}:${line}`} className="text-right"><InformationValue value={line} label={t("posting.productInformation")} plain /></div>;
    })}</div>
  </section>;
}
